import type {
  DemoSeedPhase,
  DemoDatasetSummary,
  DemoSeedProfile,
  DemoSeedProgress,
  DemoSeedStatus,
  ProfileWire,
  ProgressWire,
  DatasetSummaryWire,
} from "./types";

export const POLL_INTERVAL_MS = 750;

export const EMPTY_PROFILE: DemoSeedProfile = {
  status: null,
  datasetVersion: null,
  welcomeRequired: false,
  progress: null,
  summary: null,
};

function parseCount(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`invalid ${field} count`);
  }
  return value;
}

function parseSummary(value: unknown): DemoDatasetSummary | null {
  if (value === null) return null;
  if (typeof value !== "object") {
    throw new Error("invalid demo dataset summary");
  }
  const wire = value as DatasetSummaryWire;
  if (typeof wire.company_name !== "string" || wire.company_name.trim() === "") {
    throw new Error("invalid demo dataset company name");
  }
  // @tested-by: tst_fe_onboarding_022, tst_fe_onboarding_023
  // @invariant: profile metadata is accepted only when every checkpoint count
  // is a finite non-negative safe integer and the company is named.
  return {
    companyName: wire.company_name,
    people: parseCount(wire.people, "people"),
    companies: parseCount(wire.companies, "companies"),
    emails: parseCount(wire.emails, "emails"),
    chats: parseCount(wire.chats, "chats"),
    messages: parseCount(wire.messages, "messages"),
  };
}

function parseStatus(value: unknown): DemoSeedStatus | null {
  if (value === null) return null;
  if (value === "seeding" || value === "ready" || value === "failed") {
    return value;
  }
  throw new Error("invalid demo seed status");
}

function parsePhase(value: unknown): DemoSeedPhase {
  if (
    value === "loading_checkpoint" ||
    value === "remapping" ||
    value === "building_search_index" ||
    value === "validating" ||
    value === "finalizing" ||
    value === "complete"
  ) {
    return value;
  }
  throw new Error("invalid demo seed phase");
}

function parseProgress(value: unknown): DemoSeedProgress | null {
  if (value === null) return null;
  if (typeof value !== "object") {
    throw new Error("invalid demo seed progress");
  }
  const wire = value as ProgressWire;
  if (
    typeof wire.completed_items !== "number" ||
    !Number.isInteger(wire.completed_items) ||
    wire.completed_items < 0
  ) {
    throw new Error("invalid completed item count");
  }
  if (
    wire.total_items !== null &&
    (typeof wire.total_items !== "number" ||
      !Number.isInteger(wire.total_items) ||
      wire.total_items < wire.completed_items)
  ) {
    throw new Error("invalid total item count");
  }
  return {
    phase: parsePhase(wire.phase),
    completedItems: wire.completed_items,
    totalItems: wire.total_items,
  };
}

export function parseProfile(value: unknown): DemoSeedProfile {
  if (typeof value !== "object" || value === null) {
    throw new Error("invalid profile response");
  }
  const wire = value as ProfileWire;
  if (typeof wire.demo_welcome_required !== "boolean") {
    throw new Error("invalid demo welcome requirement");
  }
  if (
    wire.demo_dataset_version !== null &&
    typeof wire.demo_dataset_version !== "string"
  ) {
    throw new Error("invalid demo dataset version");
  }
  return {
    status: parseStatus(wire.demo_seed_status),
    datasetVersion: wire.demo_dataset_version,
    welcomeRequired: wire.demo_welcome_required,
    progress: parseProgress(wire.demo_seed_progress),
    summary: parseSummary(wire.demo_dataset_summary),
  };
}
